/**
 * Which Takeoffs the person opens from Premium's Utilities, and from Settings.
 *
 * The picker at /choose runs before sign-in, so it never calls /auth/simpro and never spends `t`.
 * The choice is kept in the browser under company:employee — Premium's launch `c` and `e`, the
 * same pair /api/me already calls companyId and simproUserId — so the next Utilities visit can
 * pre-check it. It does not skip the picker, and it is not the boolean uiBetaOptInApi.
 *
 * /auth/simpro (takeoffs-backend) redirects to /?q= or /?j= and drops every other parameter,
 * including manualView. A short-lived cookie carries the choice across that redirect so
 * BetaOptInGuard does not send the visit somewhere else. The cookie is read once per page
 * load: React strict mode runs the initializer twice, and the second read must still see it.
 */

export type TakeoffsVersion = "classic" | "new" | "ai";

export const TAKEOFFS_VERSIONS: readonly TakeoffsVersion[] = ["classic", "new", "ai"];

const STORAGE_KEY = "takeoffs.chosenVersion";
const MANUAL_COOKIE = "takeoffs.manualVersion";

export function isTakeoffsVersion(value: string | null | undefined): value is TakeoffsVersion {
	return value === "classic" || value === "new" || value === "ai";
}

/** A local setup that serves the versions elsewhere (e.g. HTTPS next to Premium) sets these. */
const APP_URL_OVERRIDES: Record<TakeoffsVersion, string | undefined> = {
	classic: process.env.REACT_APP_TAKEOFFS_CLASSIC_URL,
	new: process.env.REACT_APP_TAKEOFFS_NEW_URL,
	ai: process.env.REACT_APP_TAKEOFFS_AI_URL,
};

/** App root. Dev is cross-port; production is same-origin (/ , /beta/ , /v3/). */
export function versionAppUrl(version: TakeoffsVersion, nodeEnv = process.env.NODE_ENV): string {
	const override = APP_URL_OVERRIDES[version];
	if (override) return override;
	const prod = nodeEnv === "production";
	if (version === "classic") return prod ? "/" : "http://localhost:3002/";
	if (version === "new") return prod ? "/beta/" : "http://localhost:3003/";
	return prod ? "/v3/" : "http://localhost:3004/";
}

/**
 * AI tools signs in the way that app already does: /auth/simpro on the embed, /auth/v3/simpro
 * on Java. The local Java nginx (.v3-java) serves this app on port 8445; a build can also set
 * REACT_APP_V3_AUTH_PATH. Classic and New view always use /auth/simpro on their own host.
 */
export function versionAuthPath(
	version: TakeoffsVersion,
	loc: {port?: string} = typeof window !== "undefined" ? window.location : {}
): string {
	if (version !== "ai") return "/auth/simpro";
	if (process.env.REACT_APP_V3_AUTH_PATH === "/auth/v3/simpro" || loc.port === "8445") {
		return "/auth/v3/simpro";
	}
	return "/auth/simpro";
}

/** The launch query, unchanged, plus manualView=1. Nothing already on it is dropped or reordered. */
export function withManualView(search: string): string {
	const raw = search.startsWith("?") ? search.slice(1) : search;
	if (!raw) return "?manualView=1";
	if (/(^|&)manualView=/.test(raw)) return `?${raw}`;
	return `?${raw}&manualView=1`;
}

/** That version's sign-in URL, with the launch query appended untouched and manualView=1. */
export function versionSignInUrl(
	version: TakeoffsVersion,
	search: string,
	nodeEnv = process.env.NODE_ENV,
	loc: {port?: string} = typeof window !== "undefined" ? window.location : {}
): string {
	const app = versionAppUrl(version, nodeEnv);
	const origin = app.startsWith("http") ? new URL(app).origin : "";
	return `${origin}${versionAuthPath(version, loc)}${withManualView(search)}`;
}

/** Settings (and a guard handing a visit on): the app itself, with manualView=1 and q/j if present. */
export function versionSwitchUrl(version: TakeoffsVersion, search: string, nodeEnv = process.env.NODE_ENV): string {
	const source = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
	const params = new URLSearchParams();
	params.set("manualView", "1");
	const quote = source.get("q");
	const job = source.get("j");
	if (quote) params.set("q", quote);
	if (job) params.set("j", job);
	return `${versionAppUrl(version, nodeEnv)}?${params.toString()}`;
}

export function scopeFromCompanyEmployee(
	company: string | number | null | undefined,
	employee: string | number | null | undefined
): string | null {
	if (company == null || employee == null) return null;
	const c = String(company);
	const e = String(employee);
	if (!c || !e) return null;
	return `${c}:${e}`;
}

/** Premium's launch query: c is the company, e is the employee. Absent until sign-in otherwise. */
export function scopeFromLaunch(search: string): string | null {
	const source = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
	return scopeFromCompanyEmployee(source.get("c"), source.get("e"));
}

export function versionStorageKey(scope: string | null): string {
	return scope ? `${STORAGE_KEY}:${scope}` : STORAGE_KEY;
}

export function readChosenVersion(scope: string | null): TakeoffsVersion | null {
	try {
		const value = localStorage.getItem(versionStorageKey(scope));
		return isTakeoffsVersion(value) ? value : null;
	} catch {
		return null;
	}
}

export function writeChosenVersion(scope: string | null, version: TakeoffsVersion): void {
	try {
		localStorage.setItem(versionStorageKey(scope), version);
	} catch {
		// A private window still opens the version; the next visit just won't be pre-checked.
	}
}

export function rememberManualVisit(version: TakeoffsVersion): void {
	const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
	document.cookie = `${MANUAL_COOKIE}=${version}; Path=/; Max-Age=120; SameSite=Lax${secure}`;
}

export function readManualVisit(): TakeoffsVersion | null {
	const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${MANUAL_COOKIE}=(classic|new|ai)(?:;|$)`));
	const value = match?.[1];
	return isTakeoffsVersion(value) ? value : null;
}

export function clearManualVisit(): void {
	document.cookie = `${MANUAL_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
	document.cookie = `${MANUAL_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax; Secure`;
}

let manualVisitCache: TakeoffsVersion | null | undefined;

/** The choice that started this page load, or null. Cleared from the cookie so it cannot outlive the visit. */
export function manualVersionForThisVisit(): TakeoffsVersion | null {
	if (manualVisitCache !== undefined) return manualVisitCache;
	manualVisitCache = readManualVisit();
	if (manualVisitCache) clearManualVisit();
	return manualVisitCache;
}

export function resetManualVisitCache(): void {
	manualVisitCache = undefined;
}
